import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSession, updatePassword } from '../services/auth';
import { toast } from 'react-toastify';
import logo from '../images/logo/logo_1.png'; // Updated to original colored logo

export default function ResetPasswordPage() {
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        // Supabase will automatically parse the hash (#access_token)
        // and establish a session. If no session is established shortly,
        // it means the link is invalid or expired.
        const checkSession = async () => {
            const { data: { session } } = await getSession();
            if (!session && !window.location.hash) {
                toast.error("Enlace de recuperación inválido o expirado.");
                navigate('/login');
            }
        };
        checkSession();
    }, [navigate]);

    const handleUpdatePassword = async (e) => {
        e.preventDefault();
        
        if (password.length < 6) {
           return toast.error("La contraseña debe tener al menos 6 caracteres.");
         }

        setIsSubmitting(true);
        const { error } = await updatePassword(password);

        if (error) {
            toast.error(error.message);
        } else {
            toast.success('¡Contraseña actualizada exitosamente!');
            navigate('/dashboard');
        }
        setIsSubmitting(false);
    };

    return (
        <div className="flex min-h-screen bg-slate-50 dark:bg-boxdark-2 antialiased selection:bg-[#3245a4] selection:text-white">
            {/* Left side - Branding/Image Overlay (Hidden on Mobile) */}
            <div className="hidden lg:flex lg:w-1/2 relative bg-[#091024] overflow-hidden items-center justify-center">
                {/* Subtle background pattern or gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#3245a4] to-[#0A1930] opacity-90"></div>

                {/* Decorative Glowing Orbs */}
                <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-[#3245a4] rounded-full blur-[120px] opacity-60"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[30rem] h-[30rem] bg-[#D4AF37] rounded-full blur-[150px] opacity-20"></div>

                <div className="relative z-10 p-12 text-center max-w-lg">
                    <div className="mb-8 inline-flex items-center justify-center w-24 h-24 rounded-[30px] bg-white/10 backdrop-blur-md shadow-2xl border border-white/20">
                        <span className="material-symbols-outlined text-[48px] text-white">vpn_key</span>
                    </div>
                    <h1 className="text-4xl font-black text-white px-8 leading-tight mb-6">El Motor detrás de <br /><span className="text-[#D4AF37]">Ruta Mundial</span></h1>
                    <p className="text-blue-100/70 text-lg leading-relaxed">
                        Accede a tu plataforma central para gestionar clientes, reservar destinos asombrosos y dominar las ventas.
                    </p>
                </div>
            </div>

            {/* Right side - Form Container */}
            <div className="flex flex-col flex-1 items-center justify-center p-6 sm:p-12">
                <div className="w-full max-w-[420px] bg-white dark:bg-boxdark rounded-[24px] shadow-2xl shadow-[#3245a4]/5 dark:shadow-none border border-slate-100 dark:border-strokedark p-8 sm:p-12 transition-all duration-300 transform scale-100">

                    <div className="text-center mb-10">
                        <div className="mx-auto w-[180px] h-[50px] mb-8 bg-contain bg-center bg-no-repeat" style={{ backgroundImage: `url(${logo})` }}>
                            {!logo && <span className="font-extrabold text-[24px] text-[#3245a4] dark:text-white">RUTA MUNDIAL</span>}
                        </div>
                        <h2 className="text-2xl font-extrabold text-slate-800 dark:text-white tracking-tight">
                            Nueva Contraseña
                        </h2>
                        <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mt-2">
                            Ingresa una nueva contraseña segura para tu cuenta corporativa.
                        </p>
                    </div>

                    <form onSubmit={handleUpdatePassword} className="space-y-6">
                        <div>
                            <label className="block text-[13px] font-bold text-slate-700 dark:text-slate-300 mb-2 uppercase tracking-wide">
                                Nueva Contraseña
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <span className="material-symbols-outlined text-slate-400 text-[20px]">lock_reset</span>
                                </div>
                                <input
                                    type="password"
                                    required
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full rounded-xl border border-slate-200 dark:border-strokedark bg-slate-50 dark:bg-meta-4 py-3.5 pl-11 pr-4 text-sm text-slate-800 dark:text-white focus:ring-2 focus:ring-[#3245a4] focus:border-transparent transition-all outline-none font-medium placeholder:translate-y-1"
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="relative w-full overflow-hidden rounded-xl bg-[#3245a4] hover:bg-[#0B357F] text-white py-3.5 mt-2 font-black text-[15px] shadow-lg shadow-[#3245a4]/30 focus:outline-none focus:ring-2 focus:ring-[#3245a4] focus:ring-offset-2 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed group"
                        >
                            {isSubmitting ? (
                                <span className="flex items-center justify-center gap-2">
                                    <span className="material-symbols-outlined animate-spin text-[20px]">data_usage</span>
                                    Procesando...
                                </span>
                            ) : (
                                <span className="flex items-center justify-center gap-2">
                                    Actualizar Contraseña
                                    <span className="material-symbols-outlined text-[18px] group-hover:translate-x-1 transition-transform">
                                        check_circle
                                    </span>
                                </span>
                            )}
                        </button>
                    </form>

                    <div className="mt-10 text-center">
                        <p className="text-[11px] font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                            Uso exclusivo corporativo
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
